import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Upload, Trash2, Loader2 } from "lucide-react";

export default function BeforeAfterManager() {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [beforeFile, setBeforeFile] = useState(null);
  const [afterFile, setAfterFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    const { data } = await api.get("/before-after");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const uploadOne = async (f) => {
    const fd = new FormData();
    fd.append("file", f);
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.path;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!beforeFile || !afterFile) return toast.error("Bitte beide Bilder auswählen");
    setUploading(true);
    try {
      const [before_path, after_path] = await Promise.all([uploadOne(beforeFile), uploadOne(afterFile)]);
      await api.post("/admin/before-after", { title, description, before_path, after_path, sort_order: items.length });
      toast.success("Vorher / Nachher hinzugefügt");
      setTitle(""); setDescription(""); setBeforeFile(null); setAfterFile(null);
      document.getElementById("before-input").value = "";
      document.getElementById("after-input").value = "";
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Wirklich löschen?")) return;
    await api.delete(`/admin/before-after/${id}`);
    toast.success("Gelöscht");
    load();
  };

  return (
    <div data-testid="before-after-manager" className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <form onSubmit={handleSubmit} className="lg:col-span-1 bg-white border border-sand-300 p-6 rounded-sm space-y-4 h-fit">
        <h3 className="font-display text-2xl text-stone-900">Neues Vorher/Nachher</h3>
        <div>
          <Label className="mb-2 block">Titel</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="ba-title-input" />
        </div>
        <div>
          <Label className="mb-2 block">Beschreibung</Label>
          <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="ba-desc-input" />
        </div>
        <div>
          <Label className="mb-2 block">Vorher-Bild *</Label>
          <Input id="before-input" type="file" accept="image/*" onChange={(e) => setBeforeFile(e.target.files[0])} data-testid="ba-before-input" required />
        </div>
        <div>
          <Label className="mb-2 block">Nachher-Bild *</Label>
          <Input id="after-input" type="file" accept="image/*" onChange={(e) => setAfterFile(e.target.files[0])} data-testid="ba-after-input" required />
        </div>
        <Button type="submit" disabled={uploading} data-testid="ba-submit" className="w-full bg-stone-900 hover:bg-clay text-sand-100 rounded-sm">
          {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Lädt hoch…</> : <><Upload className="w-4 h-4 mr-2" /> Speichern</>}
        </Button>
      </form>

      <div className="lg:col-span-2">
        <h3 className="font-display text-2xl text-stone-900 mb-4">Vorher / Nachher ({items.length})</h3>
        {items.length === 0 ? (
          <p className="text-stone-600 italic" data-testid="ba-empty">Noch keine Einträge.</p>
        ) : (
          <div className="space-y-4">
            {items.map((it) => (
              <div key={it.id} data-testid={`ba-card-${it.id}`} className="bg-white border border-sand-300 rounded-sm overflow-hidden p-4">
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="relative">
                    <img src={fileUrl(it.before_path)} alt="Vorher" className="w-full aspect-[4/3] object-cover rounded-sm" />
                    <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] uppercase tracking-wider bg-stone-900 text-sand-100 rounded-sm">Vorher</span>
                  </div>
                  <div className="relative">
                    <img src={fileUrl(it.after_path)} alt="Nachher" className="w-full aspect-[4/3] object-cover rounded-sm" />
                    <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] uppercase tracking-wider bg-clay text-sand-100 rounded-sm">Nachher</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <div className="min-w-0">
                    <p className="font-medium text-stone-900">{it.title || "Ohne Titel"}</p>
                    {it.description && <p className="text-sm text-stone-600 truncate">{it.description}</p>}
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(it.id)} data-testid={`ba-delete-${it.id}`} className="text-stone-700 hover:text-clay hover:bg-sand-200">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
